<script lang="ts" module>
	import { attrs } from '#library/attrs.ts'
	import type { Blok, Fieldset, Form, Input as InputBlock } from '#library/components.schema.build.ts'
	import embla from 'embla-carousel-svelte'
	import type { EmblaCarouselType } from 'embla-carousel'
	import Field from '#components/field.svelte'
	import Button from '#components/button.svelte'
	import Checkbox from '#components/checkbox.svelte'
	import Radio from '#components/radio.svelte'
	import { enhance } from '$app/forms'
	import autoheight from 'embla-carousel-auto-height'
	import { SvelteSet } from 'svelte/reactivity'
	import type { Input } from 'storyloco/input'
	import Image from '#components/image.svelte'
	import { slide } from 'svelte/transition'
	import { page } from '$app/state'
	import { scrollto } from '#library/scrollto.ts'
	import Captcha from 'postboi/svelte'
</script>

<script lang="ts">
	interface Props {
		blok: Blok<Form>
	}

	let { blok }: Props = $props()

	let embla_api: EmblaCarouselType | undefined = $state()
	let active = $state(0)
	let form_state: 'idle' | 'submitting' | 'success' | 'error' = $state('idle')
	let form_element: HTMLFormElement | undefined = $state()
	let valid_fieldsets = new SvelteSet<number>()

	type SubmitFunction = Parameters<typeof enhance>[1]

	const submit: SubmitFunction = () => {
		form_state = 'submitting'

		return async ({ result }) => {
			switch (result.type) {
				case 'success':
					form_state = 'success'
					scrollto(blok.anchor!)
					if (blok.fieldsets?.length) embla_api?.scrollTo(blok.fieldsets?.length + 1)
					break

				case 'error':
				case 'failure':
					form_state = 'error'
					break
			}
		}
	}

	function validate_fieldsets() {
		for (const fieldset_index of Array.from(blok.fieldsets?.keys() || [])) {
			const valid = validate_fieldset(fieldset_index, false)
			if (valid) valid_fieldsets.add(fieldset_index)
			else valid_fieldsets.delete(fieldset_index)
		}
	}

	function init(event: CustomEvent) {
		embla_api = event.detail
		if (!embla_api) return

		embla_api.on('select', () => {
			scrollto(blok.anchor!)
			active = embla_api?.selectedScrollSnap() ?? 0
		})

		// Fix issue where autoheight plugin might not get the correct height
		setTimeout(carousel.reinit_for_autoheight, 100)
	}

	function get_name(input: Input, fieldset?: Fieldset) {
		const ref = input.name || ('options' in input ? input.options[0].value : undefined)
		return fieldset?.title ? [fieldset?.title, ref].join('→') : ref
	}

	function validate_fieldset(fieldset_index: number, report: boolean = true): boolean {
		if (!form_element || !blok.fieldsets) return false

		const fieldset = blok.fieldsets[fieldset_index]
		if (!fieldset?.fields) return true

		// validate only the specified fieldset
		for (const { input } of fieldset.fields) {
			if (!input) continue

			const field_name = get_name(input, fieldset)

			// handle checkboxes and radio buttons differently
			if (input.type === 'checkbox' || input.type === 'radio') {
				const field_elements = form_element.querySelectorAll(
					`[name="${field_name}"]`
				) as NodeListOf<HTMLInputElement>

				// check if any checkbox/radio in the group is required
				const has_required = Array.from(field_elements).some(el => el.required)

				// check if at_least_one is set on the input config
				const needs_at_least_one = 'at_least_one' in input && input.at_least_one

				if (has_required || needs_at_least_one) {
					let is_valid = true

					if (needs_at_least_one) {
						// for at_least_one, check if any checkbox is checked
						const is_checked = Array.from(field_elements).some(el => el.checked)
						if (!is_checked) {
							field_elements[0]?.setCustomValidity('Please select at least one option')
							if (report) field_elements[0]?.reportValidity()
							is_valid = false
						} else {
							// clear any existing validity messages
							field_elements[0]?.setCustomValidity('')
						}
					}

					if (has_required) {
						// for required checkboxes, check if each required checkbox is checked
						const required_elements = Array.from(field_elements).filter(el => el.required)

						const has_required_checked = required_elements.some(el => el.checked)
						if (!has_required_checked) {
							required_elements[0]?.setCustomValidity('This field is required')
							if (report) required_elements[0]?.reportValidity()
							is_valid = false
						} else {
							// clear validity on all required elements
							required_elements.forEach(el => el.setCustomValidity(''))
						}
					}

					if (!is_valid) return false
				}
			} else {
				// handle other input types normally
				const field_element = form_element.querySelector(`[name="${field_name}"]`) as
					| HTMLInputElement
					| HTMLSelectElement
					| HTMLTextAreaElement

				if (field_element && 'required' in input && input.required) {
					// use the field's built-in validation
					if (!field_element.checkValidity()) {
						if (report) field_element.reportValidity()
						return false
					}
				}
			}
		}

		return true
	}

	const carousel = {
		next() {
			if (validate_fieldset(active)) embla_api?.scrollNext()
		},
		previous() {
			embla_api?.scrollPrev()
		},
		goto(index: number) {
			// if trying to go forward, validate current fieldset first
			if (index > active && !validate_fieldset(active)) return

			// allow going backwards or staying on same tab
			embla_api?.scrollTo(index)
		},
		reinit_for_autoheight() {
			embla_api?.reInit()
		}
	}

	type Fields = Array<InputBlock>
</script>

{#snippet Fields(fields?: Fields, fieldset?: Fieldset)}
	{#each fields || [] as { input }, i (i)}
		{#if input}
			{@const name = get_name(input, fieldset)}
			{#if input.type !== 'checkbox' && input.type !== 'radio'}
				<Field
					instruction={input.description}
					label={input.name}
					id={name}
					{name}
					options={'options' in input ? input.options : undefined}
					placeholder={'placeholder' in input ? input.placeholder : undefined}
					required={'required' in input ? input.required : undefined}
					type={input.type}
					value={input.value}
					onchange={input.type === 'select' ? carousel.reinit_for_autoheight : undefined}
				/>
			{/if}
			{#if input.type === 'checkbox'}
				<Checkbox
					instruction={input.description}
					label={input.label}
					{name}
					options={input.options}
					at_least_one={'at_least_one' in input ? input.at_least_one : undefined}
				/>
			{/if}
			{#if input.type === 'radio'}
				<Radio
					instruction={input.description}
					label={input.name || undefined}
					{name}
					options={input.options}
					required={'required' in input ? input.required : undefined}
				/>
			{/if}
		{/if}
	{/each}
{/snippet}

{#snippet Success()}
	<div class="success">
		<div class="suptitle">{blok.success_title || 'Form submitted'}</div>
		<div class="subtitle">
			{blok.success_subtitle || 'A member of the team will be in touch as soon as possible'}
		</div>
		<Button
			type="button"
			fill
			onclick={() => {
				form_state = 'idle'
				form_element?.reset()
				embla_api?.scrollTo(0)
				active = 0
				validate_fieldsets()
			}}
		>
			Send another message
		</Button>
	</div>
{/snippet}

<section
	class:fieldsets={blok.fieldsets?.length}
	class:alt={blok.alt}
	class="form-block {blok.theme || 'theme-crocus-on-white'}"
	{...attrs(blok)}
>
	<div class="contain">
		{#if blok.alt && !blok.fieldsets?.length}
			<div class="title alt">{blok.title}</div>
		{/if}
		<header class="header">
			{#if !blok.alt || blok.fieldsets?.length}
				<div class="title">{blok.title}</div>
			{/if}
			<div class="subtitle">{blok.subtitle}</div>
			{#if blok.image?.filename}
				<Image src={blok.image.filename} alt={blok.title} focus={blok.image.focus} />
			{/if}
		</header>
		{#if blok.fieldsets?.length}
			{#if blok.fieldsets[active]?.title}
				<div class="nav_title">
					{blok.fieldsets[active].title}
					<span class="nav_paging">({active + 1}/{blok.fieldsets.length})</span>
				</div>
			{/if}
			<nav class="tabs">
				{#each blok.fieldsets || [] as fieldset, i (i)}
					<button class:active={active === i} class="tab" onclick={() => carousel.goto(i)}>
						{#if valid_fieldsets.has(i)}
							<svg class="tick">
								<use xlink:href="/images/vector.svg#check"></use>
							</svg>
						{:else}
							<span>
								{i + 1}.
							</span>
						{/if}
						{fieldset.title}
					</button>
				{/each}
			</nav>
			<div
				class="carousel"
				use:embla={{
					options: { align: 'start', watchDrag: false },
					plugins: [autoheight()]
				}}
				onemblaInit={init}
			>
				<form
					class="carousel_inner"
					method="post"
					use:enhance={submit}
					action="/?/form"
					bind:this={form_element}
					oninput={validate_fieldsets}
				>
					<input type="hidden" name="_story" value={page.data.story?.id ?? ''} />
					<input type="hidden" name="_blok" value={blok._uid} />
					{#each blok.fieldsets as fieldset, i (i)}
						{@const last = i === (blok.fieldsets?.length || 0) - 1}
						<fieldset class="fieldset">
							{#if form_state === 'error'}
								<span class="error" transition:slide>Something went wrong, please try again.</span>
							{/if}
							<legend class="fieldset_title">{fieldset.title}</legend>
							{@render Fields(fieldset.fields, fieldset)}
							{#if last}
								<Captcha />
							{/if}
							<nav class="fieldset_nav">
								{#if i > 0}
									<Button
										class="prev"
										onclick={carousel.previous}
										outline
										icon="arrow-left"
										label="Previous Step"
									></Button>
								{/if}
								{#if !last}
									<Button type="button" onclick={carousel.next} fill>Next</Button>
								{:else}
									<Button
										type="submit"
										fill
										loading={form_state === 'submitting'}
										disabled={form_state === 'success'}
									>
										{blok.submit_label}
									</Button>
								{/if}
							</nav>
						</fieldset>
					{/each}
					{@render Success()}
				</form>
			</div>
		{:else if blok.fields?.length}
			{#if form_state === 'success'}
				{@render Success()}
			{:else}
				<form
					class="form"
					method="post"
					use:enhance={submit}
					action="/?/form"
					bind:this={form_element}
					{onsubmit}
				>
					{#if form_state === 'error'}
						<span class="error" transition:slide>Something went wrong, please try again.</span>
					{/if}
					<input type="hidden" name="_story" value={page.data.story?.id ?? ''} />
					<input type="hidden" name="_blok" value={blok._uid} />
					{@render Fields(blok.fields)}
					<Captcha />
					<Button type="submit" fill loading={form_state === 'submitting'}>
						{blok.submit_label}
					</Button>
				</form>
			{/if}
		{/if}
	</div>
</section>

<style lang="css">
	.form-block {
		background-color: var(--c-background);
		padding-block: var(--padding-top, var(--padding-default)) var(--padding-bottom, var(--padding-default));
	}

	.contain {
		align-items: start;
		display: grid;
		gap: 1.5rem;

		@media (--from-tablet) {
			gap: 2.5rem;
			grid-template-columns: repeat(2, minmax(0, 1fr));

			.alt & {
				grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
			}
		}
	}

	.fieldsets {
		@media (--from-tablet) {
			.header {
				grid-column: 1 / -1;
			}
		}

		&.alt .title {
			font: 600 var(--f-h4);
			letter-spacing: var(--ls-h4);
			color: var(--c-foreground);
		}
	}

	.title {
		font: 600 var(--f-h3);
		letter-spacing: var(--ls-h3);
		margin-bottom: 1.25rem;

		&.alt {
			font: 600 var(--f-h2);
			letter-spacing: var(--ls-h2);
		}

		@media (--from-tablet) {
			grid-column: 1 / -1;

			&.alt {
				grid-column: 1 / -1;
				order: -1;
			}
		}
	}

	.subtitle {
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
		margin-bottom: 3rem;
	}

	.success {
		flex: 1 0 100%;
		align-content: start;
		background-color: var(--c-gimson);
		display: grid;
		gap: 1.5rem;
		inset: 0;
		padding: 2rem;

		.subtitle {
			margin-block-end: 1.375rem;
		}
	}

	.suptitle {
		color: var(--c-foreground);
		font: 600 var(--f-h4);
		letter-spacing: var(--ls-h4);
	}

	.error {
		color: var(--c-rose);
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
	}

	.nav_title {
		position: sticky;
		top: 3.875rem;
		background-color: var(--c-background);
		z-index: 100;
		padding: 0.5rem 0;
		display: flex;
		align-items: center;
		justify-content: space-between;
		font: 600 var(--f-h5);

		@media (--from-tablet) {
			display: none;
		}
	}

	.nav_paging {
		font: 400 var(--f-signpost);
	}

	.tabs {
		display: none;

		@media (--from-tablet) {
			align-items: start;
			display: grid;
			grid-row-start: 2;
			position: sticky;
			top: 6rem;
		}
	}

	.tab {
		display: grid;
		grid-template-columns: 2ch 1fr;
		align-items: center;
		gap: 0 1rem;
		margin-block-end: 1.75rem;
		text-align: start;
		font: 600 var(--f-h5);

		&:is(.active) {
			color: var(--c-foreground);
			text-decoration: underline;
		}

		&:not(:last-child)::after {
			content: '';
			border-block-start: 1px solid var(--c-foreground);
			top: 1.75rem;
			display: block;
			height: 1px;
			width: 100%;
			opacity: 0.4;
			grid-column: 1 / -1;
		}

		+ .tab {
			margin-block-start: 1.75rem;
		}
	}

	.tick {
		size: 1em;
		color: var(--c-foreground);
	}

	.carousel {
		overflow: hidden;

		@media (--from-tablet) {
			grid-row-start: 2;
		}
	}

	.carousel_inner {
		transition: height 0.5s ease;
		display: flex;
		align-items: start;
	}

	.fieldset,
	.form {
		margin: unset;
		flex: 1 0 100%;
		scroll-snap-align: center;
		background-color: var(--c-gimson);
		display: grid;
		gap: 1.5rem;
		padding: 1rem;

		@media (--from-tablet) {
			padding: 2rem;

			.alt & {
				order: -1;
			}
		}
	}

	.fieldset_title {
		display: contents;
		font: 400 var(--f-signpost);
		letter-spacing: var(--ls-signpost);
		text-transform: uppercase;
	}

	.fieldset_nav {
		display: flex;
		gap: 1rem;

		:global(.prev) {
			flex-shrink: 0;
		}
	}
</style>
